import { ToasterMode } from '@creditsights/styleguide-web';
import { debounce } from 'lodash';
import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import Toast from './GlobalToast';

const ALERT_TIME = 3500;

interface ToastParams {
  mode?: ToasterMode;
  textContent?: JSX.Element;
}

export interface ToastContextProviderType {
  toastParams?: ToastParams;
  showToast: (textContent: JSX.Element | string, mode?: ToasterMode) => void;
  hideToast: () => void;
}

const initialState: ToastContextProviderType = {
  showToast: () => {},
  hideToast: () => {},
};

export const ToastContext = createContext(initialState);

export const GlobalToastProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [toastParams, setToastParams] = useState<ToastParams>();

  const showToast = (textContent: JSX.Element | string, mode?: ToasterMode) => {
    debouncedChangeHandler();
    if (textContent) {
      hideToast();
    }
    const parsedTextContent = () => {
      if (typeof textContent === 'string') {
        return textContent.trim().length > 0 ? <span>{textContent.trim()}</span> : undefined;
      }
      return textContent;
    };
    // setTimeout lets hideToast(); to run before setText(text); and re-renders the component for animation purposes
    setTimeout(() => setToastParams({ textContent: parsedTextContent(), mode }));
  };

  const hideToast = () => {
    setToastParams(undefined);
  };

  const debouncedChangeHandler = useCallback(debounce(hideToast, ALERT_TIME), []);

  const toasterContextValue = useMemo(
    () => ({
      toastParams,
      hideToast,
      showToast,
    }),
    [toastParams, hideToast, showToast]
  );

  return (
    <ToastContext.Provider value={toasterContextValue}>
      <Toast />
      {children}
    </ToastContext.Provider>
  );
};
